import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize with cached user if available to prevent flash of empty state
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const cached = localStorage.getItem('cachedAuthUser');
      if (!cached) return null;
      
      const parsed = JSON.parse(cached);
      // If the cache is very old (e.g. older than 24 hours), ignore it to be safe
      const cacheTime = parsed.timestamp || 0;
      const hoursSinceCache = (Date.now() - cacheTime) / (1000 * 60 * 60);
      
      if (hoursSinceCache > 24) {
        localStorage.removeItem('cachedAuthUser');
        return null;
      }
      
      return parsed;
    } catch (e) {
      return null;
    }
  });
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Cache essential user data for faster subsequent loads
        const userDataToCache = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          timestamp: Date.now(), // Add timestamp to track cache age
        };
        localStorage.setItem('cachedAuthUser', JSON.stringify(userDataToCache));
        setCurrentUser(user);
      } else {
        localStorage.removeItem('cachedAuthUser');
        setCurrentUser(null);
      }
      setIsAuthLoading(false);
    }, (error) => {
      console.error('Auth state change error:', error);
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    const auth = getAuth();
    try {
      localStorage.removeItem('cachedAuthUser');
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    isAuthLoading,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
